import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { formatDuration } from '../utils/formatUtils';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
} from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SafeImage from './common/SafeImage';

function SessionList() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const response = await apiClient.get('/sessions');
            setSessions(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des sessions:', error);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // Si la destination est la même, ne rien faire
        if (sourceIndex === destinationIndex) return;

        const items = Array.from(sessions);
        const [reorderedItem] = items.splice(sourceIndex, 1);
        items.splice(destinationIndex, 0, reorderedItem);

        setSessions(items);

        try {
            await apiClient.put('/sessions/order', {
                sessions: items.map((session, index) => ({
                    id: session.id,
                    order: index
                }))
            });
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'ordre:', error);
            // En cas d'erreur, on restaure l'état précédent
            fetchSessions();
        }
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                    Sessions
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/sessions/new')}
                >
                    Nouvelle session
                </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sessions-droppable">
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                minHeight: '100px',
                                padding: '8px 0',
                                width: '100%'
                            }}
                        >
                            {sessions.map((session, index) => (
                                <Draggable
                                    key={String(session.id)}
                                    draggableId={String(session.id)}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                transform: provided.draggableProps.style?.transform,
                                            }}
                                        >
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    position: 'relative',
                                                    backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
                                                    boxShadow: snapshot.isDragging ? 3 : 1,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: 2,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        cursor: 'grab',
                                                        zIndex: 1,
                                                        color: snapshot.isDragging ? 'primary.main' : 'text.secondary',
                                                        '&:active': {
                                                            cursor: 'grabbing',
                                                        },
                                                    }}
                                                >
                                                    <DragIcon />
                                                </Box>
                                                <SafeImage
                                                    src={session.thumbnail}
                                                    alt={session.title}
                                                    contentType="session"
                                                    height="240px"
                                                    sx={{ borderRadius: '16px 16px 0 0' }}
                                                />
                                                <CardContent sx={{ flexGrow: 1 }}>
                                                    <Typography gutterBottom variant="h5" component="h2">
                                                        {session.title}
                                                    </Typography>
                                                    <Typography>
                                                        {session.description}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Durée: {formatDuration(session.duration)}
                                                    </Typography>
                                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => navigate(`/sessions/${session.id}/edit`)}
                                                        >
                                                            Modifier
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => navigate(`/sessions/${session.id}`)}
                                                        >
                                                            Voir détails
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </Container>
    );
}

export default SessionList; 